// Task/3424 (Refund Authenticator) - mirrors ToothTeller-Payments-API's
// Features/RefundSettlement/DTOs and Commands (same repo/task, RefundSettlementController).

export enum RefundSettlementOfferStatus {
  PendingApproval = 1,
  Approved = 2,
  Rejected = 3,
  Delivered = 4,
  Viewed = 5,
  Signed = 6,
  Voided = 7,
  Expired = 8,
}

export enum RefundSignatureMethod {
  Digital = 1,
  Manual = 2,
}

// Task/3424 follow-up (validation) - mirrors Payments-API's
// GetTransactionRefundSummaryQuery/TransactionRefundSummary DTOs.
export class ExistingRefundOfferSummary {
  refundSettlementOfferId: number;
  // What this offer takes from the payment it was looked up from.
  offerAmount: number;
  // The settlement's own amount and how many payments it covers; the confirmation shows these.
  offerTotal?: number;
  paymentCount?: number;
  status: RefundSettlementOfferStatus;
  statusName: string;
  createdOn: string;
}

export class TransactionRefundSummary {
  transactionAmount: number;
  remainingRefundableAmount: number;
  // Full refunds already made on this payment.
  fullRefundedAmount: number;
  existingOffers: ExistingRefundOfferSummary[];
  // Resolved by the API, with the last four taken from the saved card when the transaction has none.
  cardType?: string;
  lastFour?: string;
  cardLabel?: string;
  practiceLegalEntityName?: string | null;
  // Task 3537: a full refund of what is left above this limit is sent for approval instead of being processed at once.
  fullRefundApprovalLimit?: number;
  fullRefundRequiresApproval?: boolean;
  // Who it goes to; missing when no tier of the approval matrix covers the amount.
  fullRefundApproverRoleName?: string | null;
  fullRefundApproverMissing?: boolean;
}

// Concept V2 "select & allocate": how much of the settlement goes against one original payment.
export class RefundOfferAllocationInput {
  transactionId: number = 0;
  amount: number = 0;
}

// One payment inside a settlement: what is allocated to it and, once released, its linked refund.
export class RefundOfferAllocationItem {
  transactionId: number;
  transactionNumber: string;
  allocatedAmount: number;
  paidAmount: number;
  paymentDate: string;
  cardLabel: string;
  // Pending | Refunded | Failed
  status: string;
  refundTransactionId?: number | null;
  refundTransactionNumber?: string;
  refundedOn?: string | null;
  failureMessage?: string | null;
}

// A payment on the patient account with what a new settlement may still take from it.
export class RefundablePaymentItem {
  transactionId: number;
  transactionNumber: string;
  paymentDate: string;
  cardLabel: string;
  amountPaid: number;
  // What the patient was billed in total for their services (Patient & Billing Summary); the same on every row.
  billedAmount: number;
  availableToRefund: number;
  committedToSettlements: number;
  fullRefunded: number;
}

export class CreateRefundSettlementOfferCommand {
  // Single-payment form (still accepted by the API); allocations below is the multi-payment form.
  transactionId: number = 0;
  templateId: number = 0;
  offerAmount: number = 0;
  allocations: RefundOfferAllocationInput[] = [];
  reasonCodeId: number | null = null;
  reasonCodeLabel: string = '';
}

export class RefundSettlementOfferDetails {
  refundSettlementOfferId: number;
  transactionId: number;
  transactionNumber: string;
  practiceId: number;
  offerAmount: number;
  expiresOn: string;
  status: RefundSettlementOfferStatus;
  approverRoleName: string;
  allocations: RefundOfferAllocationItem[];
}

export class ApproveRefundSettlementOfferCommand {
  refundSettlementOfferId: number = 0;
  patientEmail: string | null = null;
  patientPhone: string | null = null;
}

export class RejectRefundSettlementOfferCommand {
  refundSettlementOfferId: number = 0;
  note: string = '';
}

export class RefundSigningPageDetails {
  refundSettlementOfferId: number;
  billedAmount: number;
  paidAmount: number;
  offerAmount: number;
  // With several payments: the distinct cards, comma separated; the detail is in payments.
  cardBrandLastFour: string;
  // Each payment the settlement covers (empty until the identity check).
  payments: RefundOfferAllocationItem[];
  agreementText: string;
  expiresOn: string;
  status: RefundSettlementOfferStatus;
  digitalSignatureEnabled: boolean;
  manualUploadEnabled: boolean;
  // Identity check: until the one-time code is entered the agreement text is withheld.
  identityVerified: boolean;
  // How the patient proves who they are: 'DateOfBirth' (compared with the patient record on the server) or
  // 'OneTimeCode'. The API falls back to the code when there is no date of birth on file.
  identityCheckMethod?: string;
  practiceName: string;
  maskedEmail?: string | null;
  maskedPhone?: string | null;
}

// RefundSigningCodeChannel on the API: 1 = email, 2 = text message.
export enum RefundSigningCodeChannel {
  Email = 1,
  Sms = 2,
}

export class SignRefundSettlementDigitallyCommand {
  signingToken: string = '';
  // Returned by verifySigningCode once the right one-time code has been entered.
  verificationToken: string = '';
  signerName: string = '';
  signerTitle: string | null = null;
  // IPAddress/DeviceInfo are filled in server-side from the request if left blank.
  ipAddress: string | null = null;
  deviceInfo: string | null = null;
}

export class UploadManualSignedRefundReqBody {
  refundSettlementOfferId: number = 0;
  attestationConfirmed: boolean = false;
}

export class RefundApprovalQueueItem {
  refundSettlementOfferId: number;
  // Task 3537: a full refund above the practice's limit waiting for its approver (no agreement, no patient delivery).
  isFullRefund?: boolean;
  transactionId: number;
  transactionNumber: string;
  offerAmount: number;
  reasonCodeLabel: string;
  expiresOn: string;
  status: RefundSettlementOfferStatus;
  createdBy: number;
  createdByName: string | null;
  createdOn: string;
  patientName: string;
  patientEmail: string | null;
  patientPhone: string | null;
  // Where each came from: 'Patient record' or 'Payment record' (null when there is none).
  patientEmailSource?: string | null;
  patientPhoneSource?: string | null;
  // Once approved: where the signing link went and when.
  deliveredToEmail?: string | null;
  deliveredToPhone?: string | null;
  deliveredOn?: string | null;
  // Reminders sent since (automatic and manual) and when the last one went.
  remindersSent: number;
  lastReminderOn?: string | null;
  billedAmount: number;
  paidAmount: number;
  cardBrandLastFour: string | null;
  // Each payment the settlement covers, what is allocated to it and its refund once released.
  payments: RefundOfferAllocationItem[];
  templateVersion: number | null;
  templateVersionSnapshot: string;
}

// Read-only view of the original payment behind a refund (the register's "Transaction" link).
export class RefundTransactionOfferItem {
  refundSettlementOfferId: number;
  offerAmount: number;
  // offerAmount is what this settlement allocates to THIS payment; offerTotal the whole settlement.
  offerTotal: number;
  paymentCount: number;
  status: RefundSettlementOfferStatus;
  statusName: string;
  createdOn: string;
}

export class RefundTransactionLink {
  transactionId: number;
  transactionNumber: string;
  amount: number;
  refundedOn: string;
  refundSettlementOfferId?: number | null;
  // False for an attempt the gateway turned down.
  succeeded: boolean;
}

export class RefundTransactionDetails {
  transactionId: number;
  transactionNumber: string;
  practiceId: number;
  transactionDate: string;
  patientName: string;
  amount: number;
  paymentMethod: string;
  authorizationCode?: string | null;
  invoice?: string | null;
  note?: string | null;
  statusName: string;
  // Why a failed transaction failed; null otherwise.
  failureReason?: string | null;
  // "Payment" or "Refund".
  transactionKind: string;
  refundedAmount: number;
  remainingRefundableAmount: number;
  // Set when this transaction is itself a refund: the payment it returned money on.
  refundOfTransactionId?: number | null;
  refundOfTransactionNumber?: string;
  // The refund transactions posted against this payment.
  refunds: RefundTransactionLink[];
  offers: RefundTransactionOfferItem[];
}

// The four tiles above the refund register (concept doc screen 5).
export class RefundRegisterSummary {
  // Refunds this calendar month, all types - always this month, whatever date range the table uses.
  refundedMonthToDate: number;
  // Signed / (signed + expired unsigned), whole percent; null until an offer has concluded.
  settlementAcceptanceRatePercent: number | null;
  acceptedCount: number;
  concludedCount: number;
  // Hours from an offer being created to an approver deciding it; null until one has been decided.
  averageApprovalTurnaroundHours: number | null;
  decidedCount: number;
  chargebacksOnSettledAccounts: number;
}

export class RefundRegisterItem {
  refundSettlementOfferId: number | null;
  transactionId: number;
  transactionNumber: string;
  // The most recent refund transaction: the one the refund posted as or, until it goes through, the latest failed attempt.
  refundTransactionId?: number | null;
  refundTransactionNumber?: string;
  // True when that most recent refund transaction is a failed attempt.
  refundAttemptFailed?: boolean;
  practiceId: number;
  eventTime: string;
  patientName: string;
  amount: number | null;
  isPartialSettlement: boolean;
  // The payments a settlement covers and their linked refunds (empty for full refunds).
  payments: RefundOfferAllocationItem[];
  // Refunded / Awaiting approval / Awaiting signature / Declined / Expired / Voided
  statusName: string;
  // Internal reason code - staff only.
  reasonCodeLabel?: string | null;
  // Who decided the offer, what they decided (Approved / Declined) and when; null until decided.
  approverName?: string | null;
  decisionName?: string | null;
  decidedOn?: string | null;
  // When the offer stops being valid for signing; null for full refunds.
  expiresOn?: string | null;
  // The uploaded paper copy is on file and can be downloaded.
  hasSignedDocument: boolean;
  offerStatus: RefundSettlementOfferStatus | null;
  signatureMethod: RefundSignatureMethod | null;
  hasPacket: boolean;
}

// The practice's communication templates (refund emails / texts) - served by the Payments API.
export class RefundMessageFieldItem {
  name: string;
  description: string;
}

export class RefundMessageTemplateItem {
  templateKey: string;
  label: string;
  description: string;
  // 'Email' or 'Sms'.
  channel: string;
  fields: RefundMessageFieldItem[];
  requiresAnyOf: string[];
  subject: string;
  body: string;
  isCustomized: boolean;
  defaultSubject: string;
  defaultBody: string;
  updatedOn?: string | null;
  updatedByName?: string | null;
  maxSubjectLength: number;
  maxBodyLength: number;
}

export class RefundMessagePreview {
  channel: string;
  subject: string;
  body: string;
}
